import {
  Grid,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  IconButton,
  Dialog,
  Box,
  Chip,
  Paper, // added
} from "@mui/material";
import "react-quill/dist/quill.snow.css";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../globalStyles/fonts";
import { formatDateTime } from "../../utils/formatDate";

const UsersDetailsModal = ({ users, open, onClose }) => {
  if (!users) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <DialogTitle>
            <CustomTypography variant="h6" fontWeight="600" fontSize="18px">
              User Details
            </CustomTypography>
          </DialogTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingRight: "16px",
              paddingTop: "16px",
            }}
          >
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <Divider style={{ marginBottom: "10px" }} />

        <DialogContent>
          {/* Basic Information Section */}
          <Box mb={4}>
            <CustomTypography
              variant="subtitle1"
              fontWeight="600"
              color="primary"
              mb={2}
            >
              Basic Information
            </CustomTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomTypography
                  color={"var(--text-heading)"}
                  fontSize={14}
                  fontWeight={400}
                >
                  Name
                </CustomTypography>
                <TextField
                  size="small"
                  margin="dense"
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={users?.name || ""}
                  InputProps={{
                    readOnly: true,
                    style: { color: "#000", backgroundColor: "#f5f5f5" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTypography
                  color={"var(--text-heading)"}
                  fontSize={14}
                  fontWeight={400}
                >
                  Email
                </CustomTypography>
                <TextField
                  size="small"
                  margin="dense"
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={users?.email || ""}
                  InputProps={{
                    readOnly: true,
                    style: { color: "#000", backgroundColor: "#f5f5f5" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTypography
                  color={"var(--text-heading)"}
                  fontSize={14}
                  fontWeight={400}
                >
                  Phone No
                </CustomTypography>
                <TextField
                  size="small"
                  margin="dense"
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={users?.phone_no || "Not provided"}
                  InputProps={{
                    readOnly: true,
                    style: { color: "#000", backgroundColor: "#f5f5f5" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTypography
                  color={"var(--text-heading)"}
                  fontSize={14}
                  fontWeight={400}
                >
                  Role
                </CustomTypography>

                {/* Wrapped chips in a Box styled like a read-only TextField for a clean UI */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    alignItems: "center",
                    minHeight: 40,
                    px: 1,
                    py: 0.5,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    border: "1px solid rgba(0,0,0,0.12)",
                    mt: 1,
                  }}
                >
                  {users.roles && users.roles.length > 0 ? (
                    users.roles.map((r, i) => (
                      <Chip key={i} label={r.name} size="small" />
                    ))
                  ) : (
                    <CustomTypography color="var(--text-secondary)">
                      No roles assigned
                    </CustomTypography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Roles Section */}
          <Box mb={3}>
            <CustomTypography
              variant="subtitle1"
              fontWeight="600"
              color="primary"
              mb={2}
            >
              Role Permissions
            </CustomTypography>

            {users?.roles && users.roles.length > 0 ? (
              <Box sx={{ maxHeight: "350px", overflowY: "auto" }}>
                {users.roles.map((role, index) => (
                  <Paper
                    key={index}
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      bgcolor: "#fafafa",
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <CustomTypography fontWeight="600" fontSize="16px">
                        {role.name}
                      </CustomTypography>
                      <Chip
                        label={role.active !== false ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          backgroundColor:
                            role.active !== false ? "#D7E9FF" : "#FFDEDF",
                          color: role.active !== false ? "#0070FF" : "#D13438",
                          fontWeight: 500,
                          fontSize: "12px",
                        }}
                      />
                    </Box>

                    {role.description && (
                      <CustomTypography fontSize="13px" color="#667085" mb={2}>
                        {role.description}
                      </CustomTypography>
                    )}

                    {role.permissions && role.permissions.length > 0 ? (
                      <Box sx={{ mt: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.5,
                          }}
                        >
                          <CustomTypography
                            fontSize="14px"
                            fontWeight={500}
                            color="#555"
                          >
                            Permissions
                          </CustomTypography>
                          <Chip
                            label={`${role.permissions.length} permissions`}
                            size="small"
                            sx={{
                              backgroundColor: "var(--bg-input)",
                              color: "#667085",
                              fontSize: "10px",
                            }}
                          />
                        </Box>

                        {/* Group permissions by resource type */}
                        {(() => {
                          const resourceGroups = {};

                          // Group permissions by resource
                          role.permissions.forEach((permission) => {
                            const parts = permission.split(".");
                            if (parts.length === 2) {
                              const [resource, action] = parts;
                              if (!resourceGroups[resource]) {
                                resourceGroups[resource] = [];
                              }
                              resourceGroups[resource].push(action);
                            } else {
                              // Handle non-standard format permissions
                              if (!resourceGroups["Other"]) {
                                resourceGroups["Other"] = [];
                              }
                              resourceGroups["Other"].push(permission);
                            }
                          });

                          // Define icon mapping for common actions
                          const getActionIcon = (action) => {
                            switch (action) {
                              case "create":
                                return "➕"; // Plus symbol
                              case "read":
                                return "👁️"; // Eye symbol
                              case "update":
                                return "✏️"; // Pencil symbol
                              case "delete":
                                return "🗑️"; // Trash symbol
                              case "export":
                                return "📤"; // Export symbol
                              case "import":
                                return "📥"; // Import symbol
                              case "rate-chart-calculation":
                              case "analytics":
                                return "📊"; // Analytics symbol
                              case "email-generate":
                                return "📧"; // Email symbol
                              case "ask-dropskip":
                                return "🤖"; // Robot symbol
                              case "inventory-forecast":
                                return "📈"; // Forecast symbol
                              case "package-selection":
                                return "📦"; // Package symbol
                              case "event.create":
                                return "📅"; // Calendar symbol
                              default:
                                return "";
                            }
                          };

                          // Define color mapping for action types
                          const actionColors = {
                            create: { bg: "#DCFCE7", color: "#15803D" },
                            read: { bg: "#DBEAFE", color: "#1D4ED8" },
                            update: { bg: "#FEF3C7", color: "#B45309" },
                            delete: { bg: "#FEE2E2", color: "#B91C1C" },
                            export: { bg: "#F3E8FF", color: "#7E22CE" },
                            analytics: { bg: "#E0F2FE", color: "#0284C7" },
                            "rate-chart-calculation": {
                              bg: "#E0F2FE",
                              color: "#0284C7",
                            },
                            "email-generate": {
                              bg: "#E0F2FE",
                              color: "#0284C7",
                            },
                            "ask-dropskip": { bg: "#E0F2FE", color: "#0284C7" },
                            "inventory-forecast": {
                              bg: "#E0F2FE",
                              color: "#0284C7",
                            },
                            "package-selection": {
                              bg: "#E0F2FE",
                              color: "#0284C7",
                            },
                          };

                          // Group resources into columns (2 columns on mobile, 3 on larger screens)
                          const resourceEntries = Object.entries(
                            resourceGroups,
                          ).sort(([a], [b]) => a.localeCompare(b)); // Sort resource groups alphabetically

                          return (
                            <Grid container spacing={1}>
                              {resourceEntries.map(
                                ([resource, actions], resIndex) => (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={resIndex}
                                  >
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 1,
                                        height: "100%",
                                        bgcolor: "rgba(0,0,0,0.02)",
                                        border: "1px solid rgba(0,0,0,0.08)",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <CustomTypography
                                        fontWeight={600}
                                        fontSize="13px"
                                        color="var(--text-tertiary)"
                                        sx={{
                                          mb: 0.5,
                                          borderBottom:
                                            "1px solid rgba(0,0,0,0.05)",
                                          pb: 0.5,
                                        }}
                                      >
                                        {resource.charAt(0).toUpperCase() +
                                          resource.slice(1)}
                                      </CustomTypography>

                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.5,
                                        }}
                                      >
                                        {actions
                                          .sort()
                                          .map((action, actIndex) => {
                                            const colorSet = actionColors[
                                              action
                                            ] || {
                                              bg: "#F5F5F5",
                                              color: "#666",
                                            };
                                            const actionIcon =
                                              getActionIcon(action);

                                            return (
                                              <Chip
                                                key={actIndex}
                                                icon={
                                                  actionIcon ? (
                                                    <span
                                                      style={{
                                                        fontSize: "11px",
                                                        marginRight: "4px",
                                                      }}
                                                    >
                                                      {actionIcon}
                                                    </span>
                                                  ) : undefined
                                                }
                                                label={action}
                                                size="small"
                                                sx={{
                                                  height: "25px",
                                                  fontSize: "10px",
                                                  fontWeight: 500,
                                                  backgroundColor: colorSet.bg,
                                                  color: colorSet.color,
                                                  "& .MuiChip-label": {
                                                    px: 0.5,
                                                    paddingLeft: actionIcon
                                                      ? 0
                                                      : 0.5,
                                                  },
                                                }}
                                              />
                                            );
                                          })}
                                      </Box>
                                    </Paper>
                                  </Grid>
                                ),
                              )}
                            </Grid>
                          );
                        })()}
                      </Box>
                    ) : (
                      <CustomTypography
                        fontSize="14px"
                        color="var(--text-secondary)"
                      >
                        No permissions assigned
                      </CustomTypography>
                    )}
                  </Paper>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  padding: 2,
                  bgcolor: "#f9f9f9",
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                <CustomTypography color="var(--text-secondary)">
                  No roles assigned to this user
                </CustomTypography>
              </Box>
            )}
          </Box>

          {/* Account Information */}
          {(users?.createdAt || users?.updatedAt) && (
            <Box>
              <CustomTypography
                variant="subtitle1"
                fontWeight="600"
                color="primary"
                mb={2}
              >
                Account Information
              </CustomTypography>
              <Grid container spacing={2}>
                {users?.createdAt && (
                  <Grid item xs={12} md={6}>
                    <CustomTypography
                      color={"var(--text-heading)"}
                      fontSize={14}
                      fontWeight={400}
                    >
                      Created At
                    </CustomTypography>
                    <TextField
                      size="small"
                      margin="dense"
                      variant="outlined"
                      type="text"
                      fullWidth
                      value={formatDateTime(users.createdAt)}
                      InputProps={{
                        readOnly: true,
                        style: { color: "#000", backgroundColor: "#f5f5f5" },
                      }}
                    />
                  </Grid>
                )}

                {users?.updatedAt && (
                  <Grid item xs={12} md={6}>
                    <CustomTypography
                      color={"var(--text-heading)"}
                      fontSize={14}
                      fontWeight={400}
                    >
                      Last Updated
                    </CustomTypography>
                    <TextField
                      size="small"
                      margin="dense"
                      variant="outlined"
                      type="text"
                      fullWidth
                      value={formatDateTime(users.updatedAt)}
                      InputProps={{
                        readOnly: true,
                        style: { color: "#000", backgroundColor: "#f5f5f5" },
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions style={{ padding: 16 }}>
          <Button onClick={onClose} variant="outlined" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default UsersDetailsModal;
